import React from 'react';
import { View, Text, Pressable, Platform, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { formatAddress } from '../../shared/utils/address';
import { PropertyDetail } from '../../shared/types/property';
import { Card, StatusBadge } from '../ui';
import { PropertyMap } from '../map';

// Human-friendly labels
const STATUS_LABELS: Record<PropertyDetail['status'], string> = {
  research: 'Looking At',
  under_contract: 'Buying',
  owned: 'Owned',
  listed_for_sale: 'Selling',
  sold: 'Sold',
};

interface PropertyOverviewProps {
  property: PropertyDetail;
  currentUserId?: number;
  grossIncome: number;
  totalExpenses: number;
  netCashFlow: number;
}

export function PropertyOverview({ 
  property, 
  currentUserId,
  grossIncome,
  totalExpenses,
  netCashFlow,
}: PropertyOverviewProps) {
  const styles = useStyles();
  const { isMobile, isSmallMobile, isDesktop, isLargeDesktop } = useResponsive();
  const isLaptopOrWider = isDesktop || isLargeDesktop;

  // Calculate user's share
  const userPartner = property.partners.find(p => p.user_id === currentUserId);
  const userOwnershipPercent = userPartner?.ownership_percentage || 0;
  const userShareValue = property.property_value 
    ? (property.property_value * userOwnershipPercent / 100) 
    : 0;
  const userShareCashFlow = netCashFlow * userOwnershipPercent / 100;

  const handleZillowPress = () => {
    if (!property.zillow_url) return;
    if (Platform.OS === 'web') {
      window.open(property.zillow_url, '_blank', 'noopener,noreferrer');
    } else {
      Linking.openURL(property.zillow_url);
    }
  };

  // Stat card component
  const StatCard = ({ 
    label, 
    value, 
    subtitle,
    color,
    icon,
  }: { 
    label: string; 
    value: string; 
    subtitle?: string;
    color?: string;
    icon?: keyof typeof Ionicons.glyphMap;
  }) => (
    <View style={{
      flex: isLaptopOrWider ? 1 : undefined,
      padding: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.lg,
      backgroundColor: color ? addColorOpacity(color, 0.05) : styles.tokens.color.surface,
      borderRadius: styles.utils.radius.lg,
      borderWidth: 1,
      borderColor: color ? addColorOpacity(color, 0.15) : styles.tokens.color.border,
    }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.sm, marginBottom: styles.utils.spacing.sm }}>
        {icon && (
          <Ionicons 
            name={icon} 
            size={isSmallMobile ? 16 : 20} 
            color={color || styles.tokens.color.primary} 
          />
        )}
        <Text style={[styles.text('xs'), { 
          color: addColorOpacity(styles.tokens.color.onBackground, 0.6),
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          fontWeight: styles.utils.typography.weights.semibold,
        }]}>
          {label}
        </Text>
      </View>
      <Text style={[styles.heading(isSmallMobile ? 'lg' : 'xl'), { 
        color: color || styles.tokens.color.onSurface,
        fontWeight: styles.utils.typography.weights.bold,
      }]}>
        {value}
      </Text>
      {subtitle && (
        <Text style={[styles.text('xs'), { 
          color: addColorOpacity(styles.tokens.color.onBackground, 0.5),
          marginTop: styles.utils.spacing.xs,
        }]}>
          {subtitle}
        </Text>
      )}
    </View>
  );

  return (
    <View style={{ 
      gap: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.lg,
      paddingBottom: isMobile ? styles.utils.spacing.xl : styles.utils.spacing.lg,
    }}>
      {/* Property Hero */}
      <Card 
        elevated
        style={{ 
          padding: isSmallMobile ? styles.utils.spacing.lg : styles.utils.spacing.xl,
          backgroundColor: styles.tokens.color.surface,
        }}
      >
        {/* Status Badge */}
        <View style={{ marginBottom: styles.utils.spacing.md }}>
          <StatusBadge status={property.status} size="md" />
        </View>

        {/* Address */}
        <Text 
          style={[styles.heading(isSmallMobile ? 'xl' : '2xl'), { 
            color: styles.tokens.color.onSurface,
            fontWeight: styles.utils.typography.weights.bold,
            marginBottom: styles.utils.spacing.sm,
          }]}
          numberOfLines={isMobile ? 2 : undefined}
        >
          {formatAddress(property)}
        </Text>

        {/* Property Value & Zillow Link */}
        <View style={{ 
          flexDirection: isMobile ? 'column' : 'row', 
          alignItems: isMobile ? 'flex-start' : 'center',
          gap: styles.utils.spacing.md,
          marginTop: styles.utils.spacing.md,
        }}>
          {property.property_value && (
            <Text style={[styles.heading(isSmallMobile ? '2xl' : '3xl'), { 
              color: styles.tokens.color.primary,
              fontWeight: styles.utils.typography.weights.bold,
            }]}>
              ${property.property_value.toLocaleString()}
            </Text>
          )}
          
          {property.zillow_url && (
            <Pressable
              onPress={handleZillowPress}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: styles.utils.spacing.xs,
                paddingHorizontal: styles.utils.spacing.md,
                paddingVertical: styles.utils.spacing.sm,
                backgroundColor: addColorOpacity('#006AFF', 0.1),
                borderRadius: styles.utils.radius.md,
              }}
            >
              <Ionicons name="open-outline" size={14} color="#006AFF" />
              <Text style={[styles.text('sm'), { color: '#006AFF', fontWeight: styles.utils.typography.weights.medium }]}>
                Zillow
              </Text>
            </Pressable>
          )}
        </View>

        {/* Property Details Row: Acreage, County */}
        {(property.acreage || property.county) && (
          <View style={{
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: styles.utils.spacing.md,
            marginTop: styles.utils.spacing.md,
          }}>
            {property.acreage && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.xs }}>
                <Ionicons name="resize-outline" size={16} color={styles.tokens.color.onBackground} style={{ opacity: 0.6 }} />
                <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
                  {property.acreage.toLocaleString()} acres
                </Text>
              </View>
            )}
            {property.county && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: styles.utils.spacing.xs }}>
                <Ionicons name="location-outline" size={16} color={styles.tokens.color.onBackground} style={{ opacity: 0.6 }} />
                <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
                  {property.county}
                </Text>
              </View>
            )}
          </View>
        )}
      </Card>

      {/* Property Map */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <PropertyMap
          property={property}
          height={isSmallMobile ? 280 : isMobile ? 350 : 400}
          showControls={true}
        />
      </Card>

      {/* Your Investment - Only show if user is a partner */}
      {userOwnershipPercent > 0 && (
        <Card 
          elevated
          style={{ 
            padding: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.lg,
            backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.03),
            borderWidth: 2,
            borderColor: addColorOpacity(styles.tokens.color.primary, 0.15),
          }}
        >
          <Text style={[styles.heading(isSmallMobile ? 'base' : 'lg'), { 
            color: styles.tokens.color.primary,
            fontWeight: styles.utils.typography.weights.bold,
            marginBottom: styles.utils.spacing.md,
          }]}>
            🎯 Your Investment
          </Text>
          
          <View style={{ 
            flexDirection: isLaptopOrWider ? 'row' : 'column', 
            gap: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
          }}>
            <StatCard 
              label="Your Ownership"
              value={`${userOwnershipPercent.toFixed(1)}%`}
              icon="pie-chart-outline"
              color={styles.tokens.color.primary}
            />
            {property.property_value && (
              <StatCard 
                label="Your Share Value"
                value={`$${userShareValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                subtitle={`of $${property.property_value.toLocaleString()}`}
                icon="wallet-outline"
                color={styles.tokens.color.primary}
              />
            )}
            <StatCard 
              label="Your Cash Flow"
              value={`$${userShareCashFlow.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              subtitle={userShareCashFlow >= 0 ? 'positive' : 'negative'}
              icon="trending-up-outline"
              color={userShareCashFlow >= 0 ? styles.tokens.color.success : styles.tokens.color.error}
            />
          </View>
        </Card>
      )}

      {/* Financial Summary */}
      <Card style={{ padding: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.lg }}>
        <Text style={[styles.heading(isSmallMobile ? 'base' : 'lg'), { 
          fontWeight: styles.utils.typography.weights.bold,
          marginBottom: styles.utils.spacing.md,
        }]}>
          Property Financials
        </Text>
        
        <View style={{ 
          flexDirection: isLaptopOrWider ? 'row' : 'column', 
          gap: isSmallMobile ? styles.utils.spacing.sm : styles.utils.spacing.md,
        }}>
          <StatCard 
            label="Income"
            value={`$${grossIncome.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon="arrow-down-circle-outline"
            color={styles.tokens.color.success}
          />
          <StatCard 
            label="Expenses"
            value={`$${totalExpenses.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon="arrow-up-circle-outline"
            color={styles.tokens.color.error}
          />
          <StatCard 
            label="Net Cash Flow"
            value={`$${netCashFlow.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon="cash-outline"
            color={netCashFlow >= 0 ? styles.tokens.color.success : styles.tokens.color.error}
          />
        </View>
      </Card>

      {/* Key Details */}
      <Card style={{ padding: isSmallMobile ? styles.utils.spacing.md : styles.utils.spacing.lg }}>
        <Text style={[styles.heading(isSmallMobile ? 'base' : 'lg'), { 
          fontWeight: styles.utils.typography.weights.bold,
          marginBottom: styles.utils.spacing.md,
        }]}>
          Details
        </Text>
        
        <View style={{ gap: styles.utils.spacing.sm }}>
          <View style={[styles.row, { justifyContent: 'space-between', paddingVertical: styles.utils.spacing.sm }]}>
            <Text style={[styles.text('base'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
              Status
            </Text>
            <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
              {STATUS_LABELS[property.status]}
            </Text>
          </View>
          
          {property.purchase_date && (
            <View style={[styles.row, { justifyContent: 'space-between', paddingVertical: styles.utils.spacing.sm, borderTopWidth: 1, borderTopColor: styles.tokens.color.border }]}>
              <Text style={[styles.text('base'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
                Purchase Date
              </Text>
              <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                {new Date(property.purchase_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </Text>
            </View>
          )}
          
          <View style={[styles.row, { justifyContent: 'space-between', paddingVertical: styles.utils.spacing.sm, borderTopWidth: 1, borderTopColor: styles.tokens.color.border }]}>
            <Text style={[styles.text('base'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
              Total Partners
            </Text>
            <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
              {property.partners.length}
            </Text>
          </View>
        </View>
      </Card>
    </View>
  );
}

